import { Col, Row, Modal, Space } from "antd";
import { useEffect, useState } from "react";
import PropTypes from "prop-types";

import { useSessionStore } from "../../../store/session-store.js";
import { useAxiosPrivate } from "../../../hooks/useAxiosPrivate";
import { ReviewHeader } from "../components/review-header/ReviewHeader.jsx";
import { Manage } from "../components/manage/Manage.jsx";
import { IslandLayout } from "../../../layouts/island-layout/IslandLayout.jsx";
import { DocumentViewer } from "../../../components/custom-tools/document-viewer/DocumentViewer.jsx";
import { PdfViewer } from "../../../components/custom-tools/pdf-viewer/PdfViewer.jsx";
import "../../../components/custom-tools/tool-ide/ToolIde.css";
import { ResultEditor } from "../components/result-editor/ResultEditor.jsx";
import { DownloadAndSync } from "../components/download-and-sync/DownloadAndSync.jsx";
import { EmptyState } from "../../../components/widgets/empty-state/EmptyState.jsx";
import { base64toBlob } from "../../../helpers/GetStaticData";
import { useAlertStore } from "../../../store/alert-store";
import { useExceptionHandler } from "../../../hooks/useExceptionHandler";
import { highlightText } from "../utils/highlightText";
import "./ManualReviewPage.css";

function ManualReviewPage({ type }) {
  const { sessionDetails } = useSessionStore();
  const axiosPrivate = useAxiosPrivate();
  const [classList, setClassList] = useState([]);
  const [currentReview, setCurrentReview] = useState();
  const [processedData, setProcessedData] = useState({});
  const [currentClass, setCurrentClass] = useState("");
  const [fileUrl, setFileUrl] = useState("");
  const [fileName, setFileName] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isFinalizedListOpen, setIsFinalizedListOpen] = useState(false);
  const [queueStatus, setQueueStatus] = useState({});
  const [queueData, setQueueData] = useState({});
  const [approvedList, setApprovedList] = useState([]);
  const { setAlertDetails } = useAlertStore();
  const [autoApprove, setAutoApprove] = useState(false);
  const [highlightMetadata, setHighlightMetadata] = useState({});
  const [confidenceMetadata, setConfidenceMetadata] = useState({});
  const [highlightData, setHighlightData] = useState([]);
  const handleException = useExceptionHandler();
  const path = `/api/v1/unstract/${sessionDetails.orgId.replaceAll(
    '"',
    ""
  )}/manual_review`;
  useEffect(() => {
    setCurrentReview(null);
    setIsFinalizedListOpen(false);
    setAutoApprove(false);
    const option = {
      url: `${path}/document_classes/`,
      method: "GET",
    };
    axiosPrivate(option)
      .then((result) => {
        setClassList(result?.data?.data);
      })
      .catch((err) => {
        setAlertDetails(handleException(err, "Failed to get document classes"));
      });
  }, []);

  useEffect(() => {
    setCurrentReview(null);
    setIsFinalizedListOpen(false);
    setAutoApprove(false);
  }, [currentClass, type]);

  const getQueueDetals = () => {
    const option = {
      url: `${path}/queue_details/?class_name=${currentClass}`,
      method: "GET",
    };
    axiosPrivate(option)
      .then((result) => {
        setQueueStatus(result.data);
        setIsModalOpen(true);
      })
      .catch((err) => {
        setAlertDetails(handleException(err, "Failed to get queue details"));
      });
  };

  const handleOk = () => {
    setIsModalOpen(false);
  };

  const handleCancel = () => {
    setIsModalOpen(false);
  };
  const loadFile = (position) => {
    const option = {
      url: `${path}/approved_queue_item/?class_name=${currentClass}&position=${position}`,
      method: "GET",
    };
    axiosPrivate(option)
      .then((result) => {
        updateCurrentReview(result);
        setIsFinalizedListOpen(false);
      })
      .catch((err) => {
        setAlertDetails(handleException(err, err?.response?.data?.error));
      });
  };
  const getFinalizedList = () => {
    const option = {
      url: `${path}/approved_queue_details/?class_name=${currentClass}`,
      method: "GET",
    };
    axiosPrivate(option)
      .then((result) => {
        setApprovedList(result?.data?.data);
        setIsFinalizedListOpen(true);
        setCurrentReview(false);
      })
      .catch((err) => {
        setAlertDetails(handleException(err, err.response.data.error));
      });
  };
  const pdfToBlob = (base64) => {
    const url = URL.createObjectURL(base64toBlob(base64));
    setFileUrl(url);
  };
  const updateCurrentReview = (result) => {
    // Check if the data is already parsed or needs parsing
    const rawData = result.data.data;
    // If rawData is a string and looks like it contains JSON, we parse it
    let parsedData;
    if (typeof rawData === "string") {
      try {
        parsedData = JSON.parse(rawData);
      } catch (parseError) {
        parsedData = rawData; // Fallback to raw data if parsing fails
      }
    } else {
      parsedData = rawData; // If rawData is already an object
    }
    setQueueData(parsedData);
    pdfToBlob(parsedData.file_content);
    setCurrentReview(parsedData.result); // Set the parsed data
    setHighlightMetadata(parsedData?.result?.metadata?.highlight_data || {});
    setConfidenceMetadata(parsedData?.result?.metadata?.confidence_data || {});
    setFileName(parsedData.file);
    const tableData = parsedData.result.output;
    setProcessedData(tableData);
  };

  const fetchNext = () => {
    setHighlightData([]);
    const option = {
      url: `${path}/next/?class_name=${currentClass}&type=${type}`,
      method: "GET",
    };
    axiosPrivate(option)
      .then((result) => {
        try {
          updateCurrentReview(result);
        } catch (error) {
          console.error("Error handling response data:", error); // Log any error
        }
      })
      .catch((err) => {
        setAlertDetails(handleException(err, err.response.data.error));
      });
  };

  const returnToQueue = () => {
    const body = {
      class_name: currentClass,
    };
    const option = {
      url: `${path}/return_to_queue/`,
      method: "POST",
      headers: {
        "X-CSRFToken": sessionDetails?.csrfToken,
        "Content-Type": "application/json",
      },
      data: body,
    };
    axiosPrivate(option)
      .then((result) => {
        setCurrentReview(null);
        setFileName(null);
      })
      .catch((err) => {
        setAlertDetails(
          handleException(err, "Failed to set current review data.")
        );
      });
  };

  const updateEditedData = () => {
    const body = {
      updated_data: queueData,
      class_name: currentClass,
      type: type,
    };
    const option = {
      url: `${path}/edited_data/`,
      method: "PATCH",
      headers: {
        "X-CSRFToken": sessionDetails?.csrfToken,
        "Content-Type": "application/json",
      },
      data: body,
    };
    axiosPrivate(option)
      .then((result) => {
        setAlertDetails({
          type: "success",
          content: "Succesfully updated the data.",
        });
      })
      .catch((err) => {
        setAlertDetails(handleException(err, "Failed to update data."));
      });
  };
  const checkRequiredFields = () => {
    const requiredFields = queueData?.result?.metadata?.required_fields || {};
    const output = queueData?.result?.output || {};

    const isNotNullOrEmpty = (value) => {
      if (Array.isArray(value)) {
        return value.length > 0 && value.every(isNotNullOrEmpty);
      } else if (typeof value === "object" && value !== null) {
        return (
          Object.keys(value).length > 0 &&
          Object.values(value).every(isNotNullOrEmpty)
        );
      } else {
        return value !== null && value !== undefined && value !== "";
      }
    };

    for (const [field, requiredType] of Object.entries(requiredFields)) {
      const value = output[field];

      const renderArray = (array, key, path) => {
        if (requiredType === "all") {
          if (!array.every(isNotNullOrEmpty)) {
            setAlertDetails({
              type: "error",
              content: `The required field "${key}" must have all valid entries at path "${path}".`,
            });
            return false;
          }
        } else if (requiredType === "any") {
          if (!array.some(isNotNullOrEmpty)) {
            setAlertDetails({
              type: "error",
              content: `The required field "${key}" must have at least one valid entry at path "${path}".`,
            });
            return false;
          }
        }
        return true;
      };

      const renderObject = (object, key, path) => {
        if (requiredType === "all") {
          if (!Object.values(object).every(isNotNullOrEmpty)) {
            setAlertDetails({
              type: "error",
              content: `The required field "${key}" must have all valid entries at path "${path}".`,
            });
            return false;
          }
        } else if (requiredType === "any") {
          if (!Object.values(object).some(isNotNullOrEmpty)) {
            setAlertDetails({
              type: "error",
              content: `The required field "${key}" must have at least one valid entry at path "${path}".`,
            });
            return false;
          }
        }
        return true;
      };

      const renderString = (string, key, path) => {
        if (!isNotNullOrEmpty(string)) {
          setAlertDetails({
            type: "error",
            content: `The required field "${key}" cannot be empty at path "${path}".`,
          });
          return false;
        }
        return true;
      };

      const path = field; // Path for debugging or alerts

      if (Array.isArray(value)) {
        if (!renderArray(value, field, path)) return false;
      } else if (typeof value === "object" && value !== null) {
        if (!renderObject(value, field, path)) return false;
      } else if (typeof value === "string") {
        if (!renderString(value, field, path)) return false;
      } else if (typeof value === "number") {
        if (!renderString(value.toString(), field, path)) return false;
      } else {
        if (value === undefined) {
          if (!renderString("-", field, path)) return false;
        } else {
          if (!renderString(JSON.stringify(value), field, path)) return false;
        }
      }
    }

    return true; // All required fields are valid
  };

  const finishReview = () => {
    if (!checkRequiredFields()) {
      return; // Stop if required fields validation fails
    }
    const body = {
      class_name: currentClass,
    };
    let reviewType = "finish_review";
    if (type === "approve") {
      reviewType = "approve";
    }
    const option = {
      url: `${path}/${reviewType}/`,
      method: "POST",
      headers: {
        "X-CSRFToken": sessionDetails?.csrfToken,
        "Content-Type": "application/json",
      },
      data: body,
    };
    axiosPrivate(option)
      .then((result) => {
        setCurrentReview(null);
        setAlertDetails({
          type: "success",
          content: result?.data?.message,
        });
      })
      .catch((err) => {
        setAlertDetails(handleException(err, err.response.data.error));
      });
  };

  return (
    <>
      <ReviewHeader
        title={fileName}
        classList={classList}
        fetchNext={fetchNext}
        currentReview={currentReview}
        setCurrentClass={setCurrentClass}
        currentClass={currentClass}
        returnToQueue={returnToQueue}
        getQueueDetals={getQueueDetals}
        finishReview={finishReview}
        sessionDetails={sessionDetails}
        type={type}
        getFinalizedList={getFinalizedList}
        setAutoApprove={setAutoApprove}
        autoApprove={autoApprove}
      />
      <div className="user-bg-col">
        <IslandLayout>
          {!isFinalizedListOpen && currentReview && !autoApprove && (
            <Row className="tool-ide-main">
              <Col span={12} className="tool-ide-col review-queue-col">
                <div className="tool-ide-prompts review-queue-div">
                  <DocumentViewer
                    doc={fileName}
                    isLoading={false}
                    isContentAvailable={fileUrl?.length > 0}
                    errMsg={"Failed to load file"}
                  >
                    <PdfViewer
                      fileUrl={fileUrl}
                      highlightData={highlightData}
                    />
                  </DocumentViewer>
                </div>
              </Col>
              <Col span={12} className="tool-ide-col review-queue-col">
                <div className="tool-ide-prompts review-queue-div">
                  <ResultEditor
                    data={processedData}
                    queueData={queueData}
                    setQueueData={setQueueData}
                    updateEditedData={updateEditedData}
                    type={type}
                    highlightText={highlightText}
                    highlightMetadata={highlightMetadata}
                    confidenceMetadata={confidenceMetadata}
                    setHighlightData={setHighlightData}
                  />
                </div>
              </Col>
            </Row>
          )}
          {!currentReview && isFinalizedListOpen && !autoApprove && (
            <Space
              direction="vertical"
              className="mrq-approved-list custom-space"
            >
              <DownloadAndSync
                approvedList={approvedList}
                loadFile={loadFile}
              />
            </Space>
          )}
          {autoApprove && <Manage classList={classList} />}
          {!currentReview && !isFinalizedListOpen && !autoApprove && (
            <EmptyState text="Select class and fetch next" />
          )}
        </IslandLayout>
      </div>
      <Modal
        title={"Queue Details - " + currentClass}
        open={isModalOpen}
        onOk={handleOk}
        onCancel={handleCancel}
      >
        <Row>
          <Col span={12}>Pending Review</Col>
          <Col span={12}>{queueStatus.pending_review_count}</Col>
        </Row>
        <Row>
          <Col span={12}>Review In Progress</Col>
          <Col span={12}>{queueStatus.in_progress_count}</Col>
        </Row>
        <Row>
          <Col span={12}>Reviews Finished</Col>
          <Col span={12}>{queueStatus.finished_review_count}</Col>
        </Row>
        <Row>
          <Col span={12}>Approval In Progress</Col>
          <Col span={12}>{queueStatus.approval_in_progress_count}</Col>
        </Row>
        <Row>
          <Col span={12}>Approved</Col>
          <Col span={12}>{queueStatus.approved_queue_count}</Col>
        </Row>
      </Modal>
    </>
  );
}

ManualReviewPage.propTypes = {
  type: PropTypes.string,
};

export { ManualReviewPage };
